import React from 'react';
import ScoreDial from './ScoreDial';
import { EvTag, SevPill } from './parts';
import './siteCheck.css';

// What the idle console shows before anyone checks a site.
const SAMPLE = Object.freeze({ name: 'Desert Oak Flooring', score: 54, grade: 'D', floorGrade: 'B', industry: 'flooring', town: 'Phoenix' });
const EXPECTED_STEPS = 20; // real runs emit about 18 to 27 steps
const ACTIVE = new Set(['starting', 'running']);

const capitalize = (text) => String(text || '').charAt(0).toUpperCase() + String(text || '').slice(1);

function statusText(phase, check) {
  if (ACTIVE.has(phase)) return `Reading ${(check && check.domain) || 'your site'}`;
  if (phase === 'done') {
    const report = (check && check.report) || {};
    const count = report.stats && typeof report.stats.issueCount === 'number' ? report.stats.issueCount : (report.issues || []).length;
    return `Done · ${count} finding${count === 1 ? '' : 's'}`;
  }
  if (phase === 'failed') return 'Stopped';
  return 'Ready to read a site';
}

function dotClass(phase) {
  if (ACTIVE.has(phase)) return 'bg-sky sc-pulse';
  if (phase === 'done') return 'bg-[#3DD68C]';
  if (phase === 'failed') return 'bg-[#FF6B6B]';
  return 'bg-dim';
}

function progress(phase, steps) {
  if (phase === 'done') return 100;
  if (phase === 'idle') return 0;
  return Math.min(95, (steps.length / EXPECTED_STEPS) * 100);
}

function Teaser({ sample }) {
  const industry = capitalize(sample.industry);
  return (
    <div>
      <p className="m-0 mb-3 text-[11px] uppercase tracking-[0.1em] text-dim">What you get · sample report</p>
      <div className="grid grid-cols-1 justify-items-center gap-[18px] sm:grid-cols-[120px_minmax(0,1fr)] sm:items-center sm:justify-items-stretch">
        <ScoreDial score={sample.score} grade={sample.grade} size={120} compact />
        <ul className="m-0 grid list-none gap-2 p-0 text-[13.5px]">
          <li className="flex items-center gap-2"><SevPill severity="critical">Critical</SevPill>Title does not name the city</li>
          <li className="flex items-center gap-2"><SevPill severity="critical">Critical</SevPill>No dedicated service pages</li>
          <li className="flex items-center gap-2"><SevPill severity="high">High</SevPill>Slow server response</li>
          <li className="flex items-center gap-2"><SevPill severity="medium">Floor</SevPill>{industry} sites here should be a {sample.floorGrade}</li>
        </ul>
      </div>
      <p className="mb-0 mt-[18px] text-[13.5px] text-mist">
          Bluprise opens your site like a customer and like Google, reads up to 7 pages, checks speed, search basics and
        compliance, then compares you with {sample.industry} sites in {sample.town}.
      </p>
    </div>
  );
}

function Stream({ phase, steps }) {
  const live = ACTIVE.has(phase);
  const rows = steps.length || !live ? steps : [{ text: 'Starting the check', detail: '', ev: null }];
  return (
    <ol className="m-0 grid list-none gap-0.5 p-0">
      {rows.map((step, index) => {
        const isLive = live && index === rows.length - 1;
        return (
          <li
            key={`${index}-${step.text}`}
            className="sc-rise grid grid-cols-[18px_minmax(0,1fr)_auto] items-baseline gap-2.5 border-b border-dashed border-white/10 py-[7px] text-[13.5px] last:border-b-0"
          >
            <span aria-hidden="true" className={`font-mono text-xs ${isLive ? 'text-sky' : 'text-[#3DD68C]'}`}>{isLive ? '›' : '✓'}</span>
            <span className="min-w-0 text-cream">{step.text}{step.ev ? <> <EvTag ev={step.ev} /></> : null}</span>
            <span className="max-w-[11rem] text-right font-mono text-[11.5px] text-mist [overflow-wrap:anywhere] sm:max-w-[16rem]">{step.detail}</span>
          </li>
        );
      })}
    </ol>
  );
}

function Failure({ check }) {
  const message = (check && check.error) || 'Bluprise stopped before the report was ready.';
  return (
    <div className="mt-3 text-[13.5px]">
      <p className="m-0 text-[#FF6B6B]">{message}</p>
      {/try again/i.test(message) ? null : <p className="m-0 mt-1 text-mist">Try again in a minute.</p>}
    </div>
  );
}

// The Bluprise console: a sample report while idle, then the live steps of a
// check as the API streams them, then done or stopped.
export default function LayaConsole({ phase = 'idle', check = null, sample = SAMPLE }) {
  const steps = (check && check.steps) || [];
  return (
    <aside
      aria-label="Bluprise reading your site"
      className="overflow-hidden rounded-[18px] border border-white/20 bg-[rgba(11,18,32,0.9)] shadow-[0_40px_90px_-40px_rgba(0,0,0,0.8)]"
    >
      <div className="flex items-center justify-between gap-3 border-b border-white/10 px-[18px] py-3.5">
        <div className="flex items-center gap-2.5 text-sm font-semibold">
          <span aria-hidden="true" className={`h-2 w-2 flex-none rounded-full ${dotClass(phase)}`} />
          Bluprise
        </div>
        <div aria-live="polite" className="min-w-0 truncate font-mono text-xs text-mist">{statusText(phase, check)}</div>
      </div>
      <div className="h-[3px] bg-white/10" aria-hidden="true">
        <i className="block h-full bg-gradient-to-r from-[#2F62F0] to-sky transition-[width] duration-[450ms] ease-[ease]" style={{ width: `${progress(phase, steps)}%` }} />
      </div>
      <div className="px-[18px] py-4 sm:min-h-[360px]">
        {phase === 'idle' ? <Teaser sample={sample} /> : <Stream phase={phase} steps={steps} />}
        {phase === 'failed' ? <Failure check={check} /> : null}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-white/10 px-[18px] py-3 text-xs text-dim">
        <span>Every line says how it knows: observed, inferred, estimated or unknown.</span>
      </div>
    </aside>
  );
}
